import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FindingRefs } from './findingRefSets';

/**
 * The search params a „Neu anlegen“ in a description's Verweise opens the new
 * record with — `…/finds/new?refPlanum=<id>` — so it starts out referencing
 * that description (or, for a Gesamtbeschreibung, its Befund). A search param
 * rather than router state, so the preset survives a reload.
 */
const PRESET_PARAMS: Record<keyof FindingRefs, string> = {
    planumIds: 'refPlanum',
    profileIds: 'refProfile',
    findingIds: 'refFinding'
};

/** The query string for a new record preset to reference `id`. */
export function presetRefsSearch(field: keyof FindingRefs, id: string): string {
    return `?${PRESET_PARAMS[field]}=${encodeURIComponent(id)}`;
}

/**
 * The refs a NEW record starts with, read off its URL. Null while editing and
 * for a plain new record.
 */
export function usePresetRefs(isNew: boolean): FindingRefs | null {
    const [params] = useSearchParams();
    const planumId = params.get(PRESET_PARAMS.planumIds) ?? '';
    const profileId = params.get(PRESET_PARAMS.profileIds) ?? '';
    const findingId = params.get(PRESET_PARAMS.findingIds) ?? '';
    return useMemo(() => {
        if (!isNew || !(planumId || profileId || findingId)) {
            return null;
        }
        return {
            planumIds: planumId ? [planumId] : [],
            profileIds: profileId ? [profileId] : [],
            findingIds: findingId ? [findingId] : []
        };
    }, [isNew, planumId, profileId, findingId]);
}
